import React, { useContext, useState, useEffect, useMemo } from "react";
import { CurrencyContext } from "pages/useContext";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import CloseIcon from "@mui/icons-material/Close";
import { DateRangePicker } from "react-date-range";
import moment from "moment";
import Button from "components/button";
import {
  getPackageDealPrice,
  hasRoomTypePricing,
  getRoomCombinations,
  getCombinationPrice,
  getCombinationBreakdown,
} from "utils/packageDealPrice";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./style.css";

const STOPS_OPTIONS = [
  { value: 0, label: "Non-stop" },
  { value: 1, label: "Max 1 stop" },
  { value: 2, label: "Max 2 stops" },
];

function PackageDealReserveModal({ open, onClose, deal, selectedPrice, numPeople: initialNumPeople = 1, initialSelectedCombo }) {
  const { currency } = useContext(CurrencyContext);
  const duration = deal?.duration ?? 1;
  const maxPeople = Math.min(deal?.maxParticipants || 20, 20);
  const useRoomPricing = deal && hasRoomTypePricing(deal?.price);

  const getMinDepartureDate = () => {
    if (deal?.validFrom && moment(deal.validFrom).isAfter(moment())) {
      return moment(deal.validFrom).toDate();
    }
    return moment().toDate();
  };

  const [stops, setStops] = useState(0);
  const [departureDate, setDepartureDate] = useState(getMinDepartureDate);
  const [numPeople, setNumPeople] = useState(initialNumPeople);
  const [selectedCombo, setSelectedCombo] = useState(null);

  const combinations = useMemo(
    () => (useRoomPricing && deal?.price ? getRoomCombinations(numPeople, deal.price, currency) : []),
    [useRoomPricing, numPeople, deal?.price, currency]
  );

  const combinationsWithBreakdown = useMemo(() => {
    if (!useRoomPricing || !deal?.price) return [];
    return combinations
      .map((combo) => {
        const { total, breakdown } = getCombinationBreakdown(deal.price, combo.rooms, currency);
        return {
          ...combo,
          price: total,
          breakdown,
          priceKey: combo.rooms.map((r) => `${r.type}${r.count}`).join("-"),
        };
      })
      .filter((c) => c.price > 0)
      .sort((a, b) => a.price - b.price);
  }, [useRoomPricing, deal?.price, combinations, currency]);

  useEffect(() => {
    if (open && deal) {
      setDepartureDate(getMinDepartureDate());
      setStops(0);
      setNumPeople(initialNumPeople);
      setSelectedCombo(null);
    }
  }, [open, deal?._id]);

  useEffect(() => {
    if (useRoomPricing && combinationsWithBreakdown.length > 0) {
      setSelectedCombo((prev) => {
        const match = initialSelectedCombo && combinationsWithBreakdown.find((c) => c.priceKey === initialSelectedCombo.priceKey);
        if (match) return match;
        const cheapest = combinationsWithBreakdown[0];
        if (!prev || prev.priceKey !== cheapest.priceKey) return cheapest;
        return prev;
      });
    }
  }, [useRoomPricing, numPeople, combinationsWithBreakdown, initialSelectedCombo]);

  const returnDate = moment(departureDate).add(duration, "days").toDate();
  const departureStr = moment(departureDate).format("YYYY-MM-DD");
  const returnStr = moment(returnDate).format("YYYY-MM-DD");

  const handleSearch = () => {
    const origin = deal?.origin ?? deal?.departure ?? deal?.from ?? deal?.originLocationCode ?? deal?.departureFrom;
    const destination = deal?.destination ?? deal?.arrival ?? deal?.to ?? deal?.destinationLocationCode ?? deal?.arrivalAt;

    if (!origin || !destination) return;

    const params = new URLSearchParams();
    params.set("origin", String(origin));
    params.set("destination", String(destination));
    params.set("Senior", "0");
    params.set("Adults", String(numPeople));
    params.set("flight", "Return");
    params.set("departure-date", departureStr);
    params.set("return-date", returnStr);
    params.set("refundableFare", "false");
    params.set("checkedbaggage", "false");
    params.set("stops", String(stops));
    params.set("packageDeal", "1");

    const packageAmount =
      useRoomPricing && selectedCombo
        ? selectedCombo.price
        : selectedPrice != null
        ? selectedPrice
        : getPackageDealPrice(deal?.price, currency).amount;
    params.set("packagePrice", String(packageAmount));
    params.set("packageCurrency", currency?.currency || "EUR");

    if (deal?._id) {
      params.set("packageDealId", String(deal._id));
    }

    if (useRoomPricing && selectedCombo?.rooms?.length) {
      const breakdown = { single: 0, double: 0, triple: 0, quad: 0 };
      selectedCombo.rooms.forEach(({ type, count }) => {
        if (breakdown[type] !== undefined) breakdown[type] = count;
      });
      params.set("roomSingle", String(breakdown.single));
      params.set("roomDouble", String(breakdown.double));
      params.set("roomTriple", String(breakdown.triple));
      params.set("roomQuad", String(breakdown.quad));
    }

    const searchUrl = `${window.location.origin}/search?${params.toString()}`;
    window.open(searchUrl, "_blank", "noopener,noreferrer");
    onClose();
  };

  const hasRoute = deal?.origin ?? deal?.departure ?? deal?.from ?? deal?.originLocationCode ?? deal?.departureFrom;
  const hasDestination = deal?.destination ?? deal?.arrival ?? deal?.to ?? deal?.destinationLocationCode ?? deal?.arrivalAt;
  const canSearch = hasRoute && hasDestination;
  const sign = currency?.sign ?? " € ";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          maxWidth: "900px",
        },
      }}
    >
      <DialogTitle component="div" className="pdr-modal__header">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            Search flights for this package
          </Typography>
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className="pdr-modal__content">
        <div className="pdr-modal__grid">
          <div className="pdr-modal__main">
            {/* How many travelers */}
            <div className="pdr-modal__section">
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                How many travelers?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: "0.8rem" }}>
                Select your group size from the dropdown
              </Typography>
              <FormControl
                fullWidth
                variant="outlined"
                size="medium"
                sx={{ minWidth: 180, maxWidth: 260 }}
              >
                <Select
                  value={numPeople}
                  onChange={(e) => setNumPeople(Number(e.target.value))}
                  sx={{
                    borderRadius: 2,
                    "& .MuiSelect-select": { py: 1.75, pr: 3.5 },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0,0,0,0.4)",
                      borderWidth: "2px",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "var(--primary-color, #f15e22)",
                      borderWidth: "2px",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "var(--primary-color, #f15e22)",
                      borderWidth: "2px",
                    },
                  }}
                  MenuProps={{
                    PaperProps: { sx: { borderRadius: 2, mt: 1.5 } },
                  }}
                >
                  {Array.from({ length: maxPeople }, (_, i) => i + 1).map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} {n === 1 ? "traveler" : "travelers"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Room combinations (when room-type pricing) */}
            {useRoomPricing && combinationsWithBreakdown.length > 0 && (
              <div className="pdr-modal__section">
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Room options for {numPeople} {numPeople === 1 ? "traveler" : "travelers"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Prices are per person. Select your preferred configuration.
                </Typography>
                <div className="pdr-modal__combos">
                  {combinationsWithBreakdown.map((combo) => (
                    <button
                      key={combo.priceKey}
                      type="button"
                      className={`pdr-modal__combo ${selectedCombo?.priceKey === combo.priceKey ? "pdr-modal__combo--selected" : ""}`}
                      onClick={() => setSelectedCombo(combo)}
                    >
                      <Typography fontWeight={600} fontSize="0.95rem" sx={{ mb: 1 }}>
                        {combo.label}
                      </Typography>
                      <div className="pdr-modal__combo-breakdown">
                        {combo.breakdown.map((line, i) => {
                          const people = line.occupants * line.count;
                          return (
                            <Typography key={i} variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                              {line.label}: {sign}
                              {(line.pricePerPerson % 1 === 0 ? line.pricePerPerson : line.pricePerPerson.toFixed(2))}/person × {people} {people === 1 ? "person" : "people"} = {sign}
                              {line.lineTotal % 1 === 0 ? line.lineTotal : line.lineTotal.toFixed(2)}
                            </Typography>
                          );
                        })}
                      </div>
                      <Typography fontWeight={700} color="primary.main" sx={{ mt: 1 }}>
                        Total: {sign}
                        {combo.price % 1 === 0 ? combo.price.toLocaleString() : combo.price.toFixed(2)}
                      </Typography>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Number of stops */}
            <div className="pdr-modal__section">
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Number of stops
              </Typography>
              <div className="pdr-modal__stops">
                {STOPS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`pdr-modal__stop-btn ${stops === opt.value ? "pdr-modal__stop-btn--active" : ""}`}
                    onClick={() => setStops(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Calendar - full width section */}
          <div className="pdr-modal__section pdr-modal__calendar-section">
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Select departure date
            </Typography>
            <Box className="pdr-modal__dates-summary">
              <Box className="pdr-modal__date-item">
                <Typography variant="caption" color="text.secondary">Departure</Typography>
                <Typography fontWeight={600}>{moment(departureDate).format("ddd, D MMM YYYY")}</Typography>
              </Box>
              <Box className="pdr-modal__date-arrow">→</Box>
              <Box className="pdr-modal__date-item">
                <Typography variant="caption" color="text.secondary">Return</Typography>
                <Typography fontWeight={600}>{moment(returnDate).format("ddd, D MMM YYYY")}</Typography>
                <Typography variant="caption" color="text.secondary">
                  ({duration} {duration === 1 ? "night" : "nights"} later)
                </Typography>
              </Box>
            </Box>
            <div className="pdr-modal__calendar pdr-modal__calendar--full">
              <DateRangePicker
                ranges={[
                  { startDate: departureDate, endDate: returnDate, key: "selection" },
                ]}
                onChange={(item) => setDepartureDate(item.selection.startDate)}
                rangeColors={["#f15e22"]}
                minDate={getMinDepartureDate()}
                months={2}
                direction="horizontal"
                showMonthAndYearPickers={true}
                focusedRange={[0, 0]}
                onRangeFocusChange={() => {}}
                moveRangeOnFirstSelection={false}
                retainEndDateOnFirstSelection={false}
                editableDateInputs={false}
              />
            </div>
          </div>
        </div>

        {!canSearch && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This package has no departure or destination location. Please contact us for more information.
          </Typography>
        )}
      </DialogContent>

      <DialogActions className="pdr-modal__actions">
        <Button onClick={onClose} className="pdr-modal__cancel-btn">
          Cancel
        </Button>
        <Button
          onClick={handleSearch}
          className="primary-color"
          disabled={!canSearch || (useRoomPricing && combinationsWithBreakdown.length > 0 && !selectedCombo)}
        >
          Search flights
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PackageDealReserveModal;
